<template>
  <div
    ref="bittsRadioGroupCardsRef"
    class="bitts-radio-group-cards"
    :class="{
      horizontal: orientation === 'horizontal',
    }"
  >
    <BittsFormLabel
      v-if="formLabel"
      :id="labelId"
      class="bitts-radio-group-cards__label"
      :label="formLabel"
      :disabled="disabled"
    />
    <radio-group
      :disabled="disabled"
      :value="initialValue"
      class="w-full"
      role="radiogroup"
      :aria-labelledby="formLabel ? labelId : undefined"
      @change="onChange"
    >
      <component
        :is="radioWrapperComponent(option)"
        v-for="(option, i) in options"
        :key="option.value"
        class="bitts-radio-group-cards__option"
        :class="{ 'bitts-radio-group-cards__option--hidden': option.hidden }"
        :placement="orientation === 'horizontal' ? 'top' : 'right'"
        :mount-to-body="true"
        trigger="hover"
        overlay-class="bitts-radio-group-tooltip"
      >
        <BittsRadio :option="option" :options="options" :index="i">
          <template #label>
            <slot name="label" :option="option">
              {{ option.label }}
            </slot>
          </template>
          <template #right>
            <slot name="right" :option="option" />
          </template>
        </BittsRadio>
        <template v-if="option.tooltip" #title>
          <slot name="tooltip" :option="option">
            {{ option.tooltip }}
          </slot>
        </template>
        <template v-else-if="option.popover" #content>
          <slot name="popover" :option="option">
            {{ option.popover }}
          </slot>
        </template>
      </component>
    </radio-group>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="TOption extends RadioOptionType = RadioOptionType"
>
import { Radio } from 'ant-design-vue';
import type { RadioChangeEvent } from 'ant-design-vue/es/radio/interface';
import { onMounted, onUnmounted, ref, useId } from 'vue';

import BittsFormLabel from '../BittsFormLabel/BittsFormLabel.vue';
import { FormLabelType } from '../BittsFormLabel/types';
import BittsPopover from '../BittsPopover/BittsPopover.vue';
import BittsRadio from '../BittsRadio/BittsRadio.vue';
import { RadioOptionType } from '../BittsRadio/types';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';

type Props = {
  options?: TOption[];
  initialValue?: TOption['value'];
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  formLabel?: FormLabelType | string;
};

withDefaults(defineProps<Props>(), {
  options: () => [],
  initialValue: undefined,
  disabled: false,
  orientation: 'vertical',
  formLabel: '',
});

const emit = defineEmits<{
  change: [value: TOption['value']];
}>();

const labelId = useId();

const RadioGroup = Radio.Group;

function onChange(e: RadioChangeEvent) {
  emit('change', e.target.value as TOption['value']);
}

function radioWrapperComponent(option: TOption) {
  if (option.tooltip) return BittsTooltip;
  if (option.popover) return BittsPopover;
  return 'div';
}

/* We can't control the background color of a wrapper div in Bitts
when an ANT DOM element gets focus without vanilla Javascript.
I'm initializing some event listeners that will manually attach
the right styles when the focus changes. */
const bittsRadioGroupCardsRef = ref<HTMLDivElement | null>(null);
let elements: HTMLInputElement[] = [];

const setOrLoseFocus = (focus: boolean) => (el: FocusEvent) => {
  const parent = (el.target as HTMLElement | null)?.closest(
    '.ant-radio-wrapper',
  );
  if (!parent) return;
  if (focus) parent.classList.add('bg-neutral-50');
  else parent.classList.remove('bg-neutral-50');
};

const blurHandler = setOrLoseFocus(false);
const focusHandler = setOrLoseFocus(true);
onMounted(() => {
  elements = Array.from(
    bittsRadioGroupCardsRef.value?.querySelectorAll('input') ?? [],
  );
  for (const el of elements) {
    el.addEventListener('focus', focusHandler);
    el.addEventListener('blur', blurHandler);
  }
});

onUnmounted(() => {
  for (const el of elements) {
    el.removeEventListener('focus', focusHandler);
    el.removeEventListener('blur', blurHandler);
  }
});
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-radio-group-cards__label {
  @apply text-neutral-text-strong text-base h-24;
}

.bitts-radio-group-cards {
  .ant-radio-group {
    font-family: inherit;
  }
  .c-bitts-radio {
    &:hover:not(.ant-radio-wrapper-disabled) {
      @apply bg-neutral-50;
    }
  }

  .ant-radio-wrapper {
    @apply flex items-center py-8 px-16 mr-0 border-neutral-border border-solid mb-16;
    font-family: inherit;
    /* The "right" slot */
    > span:last-of-type {
      @apply flex items-center justify-between w-full;
    }
  }

  .ant-radio-wrapper .ant-radio-inner::after {
    @apply h-[22px] w-[22px] ms-[-11px];
    margin-block-start: -11px;
  }

  .bitts-radio-group-cards__option-label {
    @apply text-neutral-text-strong;
  }

  .bitts-radio-group-cards__option {
    @apply flex-1;
  }

  .bitts-radio-group-cards__option--hidden {
    @apply hidden;
  }

  .bitts-radio-group-cards__description {
    @apply text-neutral-text text-sm;
  }

  .ant-radio-wrapper {
    @apply mb-0 border border-b-0;
  }

  .ant-radio-wrapper.first {
    @apply rounded-t-lg border-t;
  }

  .ant-radio-wrapper.last {
    @apply border-b rounded-b-lg;
  }

  .ant-radio {
    @apply mr-8;
  }
}

/* Horizontal alignment */
.bitts-radio-group-cards.horizontal {
  .ant-radio-group-outline {
    @apply flex rounded-l-lg;

    .ant-radio-wrapper {
      @apply border-t border-b h-full;
    }
    .ant-radio-wrapper.first {
      @apply rounded-l-lg rounded-r-none border-r-0;
    }
    .ant-radio-wrapper.last {
      @apply rounded-r-lg rounded-l-none;
    }
    .ant-radio-wrapper.only {
      @apply rounded-lg border;
    }
  }
}
</style>
